import type { AssignmentConfig, SiteMetadata, TestResults } from './evaluator-types.ts';
import interactiveJava from './interactive-java.ts';
import serverEvaluator from './server-evaluator.ts';
import type { Message } from './terminal-engine.ts';

/*
 * Interactive Java assignments that are also unit-test graded
 * (plans/interactive-program-tests.md's conversation-tested assignments,
 * layouts/java-terminal-testcases.njk): the student plays their own program
 * in the Terminal tab and the graded pills live in the Tests tab. Composes
 * the two existing evaluators rather than reimplementing either: the run
 * button follows the visible tab exactly as it does for JShell — on Terminal
 * (or JShell) the editor code goes to interactive-java.ts, on Tests it goes
 * to the grader (server-evaluator.ts) and the results land in the pills.
 *
 * A terminal run resolves undefined, which index.ts's applyResults treats as
 * "leave the result display alone": playing your game neither clears nor
 * updates your last test results.
 */
class InteractiveJavaTested {
  interactive: ReturnType<typeof interactiveJava>;
  tests: ReturnType<typeof serverEvaluator>;

  constructor(
    config: AssignmentConfig & { server: NonNullable<AssignmentConfig['server']> },
    message: Message,
    username: string,
    metadata: SiteMetadata,
  ) {
    this.interactive = interactiveJava(config, message, username, metadata);
    this.tests = serverEvaluator(config, message, username, metadata);
  }

  async execute(
    code: string,
    filename: string,
    others: { file: string; content: string }[] = [],
  ): Promise<TestResults | undefined> {
    if (this.interactive.activeTab === 'tests') {
      return (await this.tests.execute(code, filename, others)) as TestResults;
    }
    await this.interactive.execute(code, filename, others);
    return undefined;
  }
}

const evaluator = (
  config: AssignmentConfig & { server: NonNullable<AssignmentConfig['server']> },
  message: Message,
  username: string,
  metadata: SiteMetadata,
) => new InteractiveJavaTested(config, message, username, metadata);

export default evaluator;
